import React, { Component, createRef, RefObject } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';

import { getTailwindColor } from '@trqk/utils';
import { unstyleScrollbar } from '../helpers';

/**
 * @typedef {SourceEarningsRangesProps}
 * @property {string} chartDivId a unique ID for this chart instance
 * @property {number} height height for the chart div
 * @property {ProSource[]} sources array of ProSource elements to render
 * @property {string} valueYMin the key for the min value of the range on the ProSource
 * @property {string} valueYMax the key for the max value of the range on the ProSource
 * @property {string} valueAxisLabel a label for the value axis
 */
interface SourceEarningsRangesProps {
    chartDivId: string;
    height: number;
    sources: ProSource[];
    valueYMin: string;
    valueYMax: string;
    valueAxisLabel: string;
}

const categoryKey = 'source_name';
const valueAxisLabelColor = getTailwindColor('tertiary-400');

export class SourceEarningsRanges extends Component<SourceEarningsRangesProps> {
    private chart: am4charts.XYChart | null = null;

    // There is a race condition in amCharts which can cause issues if we use a chart div ID rather
    // than a ref. The chartDivId prop is only used as a fallback for type safety; the ref
    // should be available by the time componentDidMount is called.
    private divRef: RefObject<HTMLDivElement> | null = null;

    constructor(props: SourceEarningsRangesProps) {
        super(props);

        this.divRef = createRef();
    }

    componentDidMount(): void {
        am4core.createDeferred((): am4core.Sprite => {
            const {
                chartDivId,
                sources,
                valueAxisLabel,
                valueYMin,
                valueYMax,
            } = this.props;

            const chart = am4core.create(
                this.divRef?.current ?? chartDivId,
                am4charts.XYChart,
            );
            const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
            const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
            const series = chart.series.push(new am4charts.ColumnSeries());
            const openBullet = series.bullets.create(am4charts.CircleBullet);
            const closeBullet = series.bullets.create(am4charts.CircleBullet);

            chart.data = sources;
            chart.numberFormatter.numberFormat = '$#,###.00';
            chart.fontSize = '0.8rem';

            categoryAxis.renderer.grid.template.location = 0;
            categoryAxis.dataFields.category = categoryKey;
            categoryAxis.renderer.minGridDistance = 0.01;
            categoryAxis.renderer.grid.template.location = 0.5;
            categoryAxis.renderer.grid.template.strokeDasharray = '1,3';
            categoryAxis.renderer.labels.template.location = 0.5;
            categoryAxis.renderer.labels.template.fullWords = false;
            categoryAxis.renderer.labels.template.horizontalCenter = 'right';
            categoryAxis.renderer.labels.template.maxWidth = 120;
            categoryAxis.renderer.labels.template.rotation = -45;
            // eslint-disable-next-line no-template-curly-in-string
            categoryAxis.renderer.labels.template.tooltipText = '{category}';
            categoryAxis.renderer.labels.template.truncate = true;
            categoryAxis.renderer.labels.template.tooltipX =
                am4core.percent(100);
            categoryAxis.renderer.labels.template.tooltipY = am4core.percent(0);

            if (categoryAxis.tooltip !== undefined) {
                categoryAxis.tooltip.label.maxWidth = 200;
                categoryAxis.tooltip.label.wrap = true;
            }

            categoryAxis.renderer.inside = false;

            categoryAxis.renderer.labels.template.adapter.add(
                'dx',
                (dx, target) => -target.maxRight / 2,
            );

            valueAxis.title.text = valueAxisLabel;

            if (valueAxis.tooltip !== undefined) {
                valueAxis.tooltip.disabled = true;
            }

            valueAxis.renderer.labels.template.fill =
                am4core.color(valueAxisLabelColor);
            valueAxis.renderer.ticks.template.disabled = true;
            valueAxis.renderer.axisFills.template.disabled = true;

            series.dataFields.categoryX = categoryKey;
            series.dataFields.openValueY = valueYMin;
            series.dataFields.valueY = valueYMax;
            // eslint-disable-next-line no-template-curly-in-string
            series.tooltipText = 'min: {openValueY.value} max: {valueY.value}';
            series.sequencedInterpolation = true;
            series.fillOpacity = 0;
            series.strokeOpacity = 1;
            series.columns.template.width = 0.01;

            if (series.tooltip !== undefined) {
                series.tooltip.pointerOrientation = 'horizontal';
            }

            // This line is taken directly from an amCharts example, so they must have a type definition
            // error.
            // eslint-disable-next-line
            // @ts-ignore
            openBullet.locationY = 1;

            closeBullet.fill = chart.colors.getIndex(4);
            closeBullet.stroke = closeBullet.fill;

            chart.cursor = new am4charts.XYCursor();

            chart.scrollbarX = new am4core.Scrollbar();
            chart.scrollbarY = new am4core.Scrollbar();

            unstyleScrollbar(chart.scrollbarX);
            unstyleScrollbar(chart.scrollbarY);

            chart.zoomOutButton.disabled = true;

            chart.events.on('ready', (): void => {
                categoryAxis.zoomToIndexes(0, 10, false, true);
            });

            this.chart = chart;

            return chart;
        }, this);
    }

    componentDidUpdate(prevProps: SourceEarningsRangesProps): void {
        const { sources } = this.props;

        if (prevProps.sources !== sources) {
            if (this.chart !== null) {
                this.chart.data = sources;
            }
        }
    }

    componentWillUnmount(): void {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    render(): JSX.Element {
        const { chartDivId, height } = this.props;

        return (
            <div
                id={chartDivId}
                ref={this.divRef}
                style={{ width: '100%', height }}
                data-cy="source-earnings-ranges-chart"
            />
        );
    }
}
